<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useStorage } from '@vueuse/core';
import { match } from 'ts-pattern';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

import LocaleSelector from '@/components/LocaleSelector.vue';
import { TButton } from '@/components/ui/button';
import { TCheckbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxList,
  ComboboxItemIndicator,
  ComboboxItem,
} from '@/components/ui/combobox';
import {
  TDialogHeader,
  TDialog,
  TDialogContent,
  TDialogContentBodyCentered,
  TDialogClose,
} from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { RadioGroup, RadioGroupCard } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { TWaveLoader } from '@/components/ui/wave-loader';
import { useIntl } from '@/hooks/use-intl';
import { useUpdateUserMutation } from '@/queries/users/use-user-update-mutation.ts';
import { useUserMeQuery } from '@/queries/users/use-users-me-query';
import { PerformanceImpactRatio } from '@/types/users/performance-impact-ratio-enum';

import CheckIcon from '~icons/iconoir/check';
import NavArrowDownIcon from '~icons/iconoir/nav-arrow-down';

const { t: $t } = useI18n();
const { currencyFormat } = useIntl();

const { data: user, refetch } = useUserMeQuery();
const { mutateAsync: updateUserMutation } = useUpdateUserMutation();

const zodSchema = z.object({
  financialWealth: z.string(),
  investmentIntent: z.string(),
  allowNewsletter: z.boolean(),
  sources: z.array(z.string()).min(0),
  performanceImpactRatio: z.nativeEnum(PerformanceImpactRatio),
});

const formSchema = toTypedSchema(zodSchema);

const { handleSubmit, isSubmitting, meta, values, setFieldValue } = useForm({
  validationSchema: formSchema,
  initialValues: {
    sources: [],
    allowNewsletter: false,
    performanceImpactRatio: PerformanceImpactRatio.BOTH,
  },
});

const INVESTOR_PROFILE_KEY = 'dismiss-investor-profile';
const state = useStorage(INVESTOR_PROFILE_KEY, '', localStorage);
const open = computed(() => !state.value && !!user.value && !user.value.financialWealth);
const isFormValid = computed(() => meta.value.valid);
const isButtonDisabled = computed(() => !isFormValid.value || isSubmitting.value);
const isSubmitPending = ref(false);

const sourceOptions = [
  'PODCAST',
  'ARTICLES',
  'LINKEDIN',
  'SEARCH_ENGINE',
  'HOMAIO_CONTRIBUTOR',
  'RELATIVES',
  'EVENT',
  'OTHER',
  'NL_SNOWBALL',
] as const;

const sourceLabels = {
  PODCAST: $t('common.discoverSource.podcast'),
  ARTICLES: $t('common.discoverSource.article'),
  LINKEDIN: $t('common.discoverSource.linkedIn'),
  SEARCH_ENGINE: $t('common.discoverSource.searchEngine'),
  HOMAIO_CONTRIBUTOR: $t('common.discoverSource.homaioContributor'),
  RELATIVES: $t('common.discoverSource.relatives'),
  EVENT: $t('common.discoverSource.event'),
  OTHER: $t('common.discoverSource.other'),
  NL_SNOWBALL: $t('common.discoverSource.nlSnowball'),
} as const;

const onSubmit = handleSubmit(async (formData) => {
  if (!user.value) {
    return;
  }
  await updateUserMutation({
    user: formData,
    userId: user.value.id,
  });

  return refetch();
});

const dataToRatio = (val: PerformanceImpactRatio) => {
  return match(val)
    .with(PerformanceImpactRatio.PERFORMANCE_2, () => [0])
    .with(PerformanceImpactRatio.PERFORMANCE_1, () => [1])
    .with(PerformanceImpactRatio.BOTH, () => [2])
    .with(PerformanceImpactRatio.IMPACT_1, () => [3])
    .with(PerformanceImpactRatio.IMPACT_2, () => [4])
    .otherwise(() => [2]);
};

const ratioToData = (val: [0] | [1] | [2] | [3] | [4]) => {
  return match(val)
    .with([0], () => PerformanceImpactRatio.PERFORMANCE_2)
    .with([1], () => PerformanceImpactRatio.PERFORMANCE_1)
    .with([2], () => PerformanceImpactRatio.BOTH)
    .with([3], () => PerformanceImpactRatio.IMPACT_1)
    .with([4], () => PerformanceImpactRatio.IMPACT_2)
    .exhaustive();
};

watch([user], () => {
  if (user.value) {
    if (user.value.financialWealth) {
      setFieldValue('financialWealth', user.value.financialWealth);
    }
    if (user.value.investmentIntent) {
      setFieldValue('investmentIntent', user.value.investmentIntent);
    }
    if (user.value.allowNewsletter) {
      setFieldValue('allowNewsletter', user.value.allowNewsletter);
    }
    if (user.value.sources) {
      setFieldValue('sources', user.value.sources);
    }
    if (user.value.performanceImpactRatio) {
      setFieldValue('performanceImpactRatio', user.value.performanceImpactRatio);
    }
  }
});
</script>

<template>
  <TDialog v-model:open="open">
    <TDialogContent class="bg-beige-100">
      <TDialogHeader class="flex flex-row justify-between bg-white lg:bg-beige-100">
        <div class="h-full w-fit content-center text-primary-900">
          <LocaleSelector />
        </div>
        <template #modal-close />
      </TDialogHeader>
      <TDialogContentBodyCentered class="gap-3">
        <div class="mb-7 flex flex-col gap-2">
          <h4 class="font-bold leading-9">
            {{ $t('dialog.investorProfile.title') }}
          </h4>
        </div>
        <form class="flex flex-col gap-10" @submit.prevent="() => onSubmit()">
          <FormField v-slot="{ componentField }" name="sources">
            <FormItem>
              <Combobox
                by="value"
                multiple
                class="w-full"
                :model-value="componentField['modelValue']"
                @update:model-value="componentField['onUpdate:modelValue']"
              >
                <FormControl>
                  <ComboboxAnchor
                    class="inline-flex h-10 min-h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-primary-400 bg-white px-4 py-2 text-ui-m font-bold text-primary-800 ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:opacity-50"
                  >
                    <ComboboxTrigger class="flex h-full w-full items-center justify-between">
                      <span
                        class="no-scrollbar flex-1 overflow-scroll text-left text-sm font-medium"
                        :class="{
                          'text-primary-500': componentField.modelValue.length === 0,
                          'text-primary-600': componentField.modelValue.length > 0,
                        }"
                      >
                        {{
                          componentField.modelValue.length > 0
                            ? componentField.modelValue
                                .map?.((source: keyof typeof sourceLabels) => sourceLabels[source])
                                .join(', ')
                            : $t('dialog.investorProfile.sourceLabel')
                        }}
                      </span>
                      <NavArrowDownIcon />
                    </ComboboxTrigger>
                  </ComboboxAnchor>
                </FormControl>
                <ComboboxList class="w-full space-y-3 bg-white">
                  <ComboboxItem
                    v-for="source in sourceOptions"
                    :key="source"
                    :value="source"
                    class="mx-4 flex items-center gap-2 pl-8 first:mt-4 last:mb-4"
                  >
                    <ComboboxItemIndicator
                      class="absolute left-0 inline-flex w-[25px] items-center justify-center"
                    >
                      <CheckIcon />
                    </ComboboxItemIndicator>
                    {{ sourceLabels[source] }}
                  </ComboboxItem>
                </ComboboxList>
              </Combobox>
            </FormItem>
          </FormField>
          <div class="flex flex-col gap-4">
            <label class="text-sm font-medium leading-none text-primary-700">
              {{ $t('dialog.investorProfile.profileRatioLabel') }}
            </label>
            <div class="flex flex-row gap-2.5 text-xs font-medium text-primary-500">
              <span>{{ $t('dialog.investorProfile.performance') }}</span>
              <Slider
                :model-value="dataToRatio(values.performanceImpactRatio!)"
                :max="4"
                :step="1"
                @update:model-value="
                  (val) => {
                    setFieldValue(
                      'performanceImpactRatio',
                      ratioToData(val as [0] | [1] | [2] | [3] | [4]),
                    );
                  }
                "
              />
              <span>{{ $t('dialog.investorProfile.impact') }}</span>
            </div>
          </div>
          <FormField
            v-slot="{ componentField }"
            name="financialWealth"
            as="div"
            class="flex flex-col gap-4"
          >
            <FormLabel class="font-medium text-primary-700">
              {{ $t('dialog.investorProfile.wealthLabel') }}
            </FormLabel>
            <FormItem>
              <FormControl>
                <RadioGroup
                  class="grid grid-cols-1 text-sm font-medium sm:grid-cols-2"
                  v-bind="componentField"
                >
                  <RadioGroupCard value="wealth_range_1" class="px-4 py-3 text-left">
                    {{
                      $t('common.lessThan', {
                        amount: currencyFormat(100_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="wealth_range_2" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(100_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(500_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="wealth_range_3" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(500_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(1_000_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="wealth_range_4" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(1_000_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(5_000_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="wealth_range_5" class="px-4 py-3 text-left">
                    {{
                      $t('common.moreThan', {
                        amount: currencyFormat(5_000_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                </RadioGroup>
              </FormControl>
            </FormItem>
          </FormField>
          <FormField
            v-slot="{ componentField }"
            name="investmentIntent"
            as="div"
            class="flex flex-col gap-4"
          >
            <FormLabel class="font-medium text-primary-700">
              {{ $t('dialog.investorProfile.investmentLabel') }}
            </FormLabel>
            <FormItem>
              <FormControl>
                <RadioGroup
                  class="grid grid-cols-1 text-sm font-medium sm:grid-cols-2"
                  v-bind="componentField"
                >
                  <RadioGroupCard value="intent_range_1" class="px-4 py-3 text-left">
                    {{
                      $t('common.lessThan', {
                        amount: currencyFormat(1_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="intent_range_2" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(1_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(5_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="intent_range_3" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(5_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(20_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="intent_range_4" class="px-4 py-3 text-left">
                    {{
                      $t('common.between', {
                        amount1: currencyFormat(20_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                        amount2: currencyFormat(100_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                  <RadioGroupCard value="intent_range_5" class="px-4 py-3 text-left">
                    {{
                      $t('common.moreThan', {
                        amount: currencyFormat(100_000, {
                          currency: 'EUR',
                          notation: 'compact',
                        }),
                      })
                    }}
                  </RadioGroupCard>
                </RadioGroup>
              </FormControl>
            </FormItem>
          </FormField>
          <div class="flex gap-2">
            <FormField v-slot="{ value, handleChange }" type="checkbox" name="allowNewsletter">
              <FormItem class="flex flex-row items-start justify-start gap-2">
                <FormControl>
                  <TCheckbox
                    class="mt-[2px] border-primary-600"
                    :model-value="value"
                    @update:model-value="handleChange"
                  />
                </FormControl>
                <FormLabel class="!mt-0 text-sm font-medium text-primary-700">{{
                  $t('dialog.investorProfile.offerDescription')
                }}</FormLabel>
              </FormItem>
            </FormField>
          </div>
          <div class="flex flex-col gap-2">
            <TButton type="submit" :disabled="isButtonDisabled">
              <TWaveLoader v-if="isSubmitPending" size="sm" class="bg-white" />
              <p v-else>{{ $t('dialog.investorProfile.submitButton') }}</p>
            </TButton>
            <TDialogClose as-child>
              <TButton type="button" variant="link" :onclick="() => (state = 'true')">{{
                $t('dialog.investorProfile.skipButton')
              }}</TButton>
            </TDialogClose>
          </div>
        </form>
      </TDialogContentBodyCentered>
    </TDialogContent>
  </TDialog>
</template>
